import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import * as StreamChatService from '@cobu/stream-chat';
import type { StreamUser } from '@cobu/types';
import { STREAM_CHAT_SORT } from '../constants';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';

// Conditional imports for Stream Chat React Native components
let Chat: any = null;
let ChannelList: any = null;
let Channel: any = null;
let MessageList: any = null;
let MessageInput: any = null;
let OverlayProvider: any = null;
let i18nEs: any = null;

const loadStreamComponents = async () => {
  if (Chat && ChannelList && OverlayProvider) return;
  try {
    const streamComponents = await import('stream-chat-react');
    const sc: any = streamComponents as any;
    Chat = sc.Chat;
    ChannelList = sc.ChannelList;
    Channel = sc.Channel;
    MessageList = sc.MessageList;
    MessageInput = sc.MessageInput;
    OverlayProvider = sc.OverlayProvider;
    i18nEs = new sc.Streami18n({ language: 'es' });
  } catch (error) {
    logger.warn('Stream Chat React components failed to load:', error);
  }
};


interface UseChannelListProps {
  enabled?: boolean;
}

interface UseChannelListReturn {
  chatClient: any | null;
  isLoading: boolean;
  error: string | null;
  isStreamAvailable: boolean;
  // Components (null on web)
  Chat: any;
  ChannelList: any;
  Channel: any;
  MessageList: any;
  MessageInput: any;
  OverlayProvider: any;
  i18nInstance: any;
  // Functions
  navigateToChannel: (targetUserId: string, displayName: string) => Promise<any>;
  refreshChannels: () => void;
}

export const useChannelList = ({
  enabled = true,
}: UseChannelListProps = {}): UseChannelListReturn => {
  const [chatClient, setChatClient] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user data from Redux store
  const { user, streamToken, language } = useSelector((state: RootState) => state.auth);

  // Pass Spanish i18n instance only when language is Spanish; null = GetStream default (English)
  const i18nInstance = language === 'es' ? i18nEs : null;

  // Check if Stream Chat is available for current platform
  const isStreamAvailable = useMemo(() => StreamChatService.isStreamChatAvailable(), []);

  // Memoized user object for Stream Chat
  const streamUser: StreamUser | null = useMemo(() => {
    if (!user) return null;

    return {
      uuid: user.uuid,
      displayName: user.displayName || `${user.firstName} ${user.lastName}`,
      profileImage: user.profileImage || '',
    };
  }, [user]);

  // Memoized channel filters (from legacy app)
  const channelFilters = useMemo(() => ({
    type: 'messaging',
    members: { $in: [user?.uuid] },
  }), [user?.uuid]);

  // Initialize Stream Chat client
  const initializeStreamChat = useCallback(async () => {
    // Allow lazy initialization even when hook was created with enabled: false
    if (!isStreamAvailable) {
      throw new Error('Stream chat not available');
    }
    if (!streamUser || !streamToken) {
      throw new Error('Chat client not ready');
    }
    // If already connected, return existing client
    if (chatClient) return chatClient;

    setIsLoading(true);
    setError(null);

    try {
      await loadStreamComponents();
      const client = await StreamChatService.getStreamChatClient(streamUser, streamToken);
      setChatClient(client);

      // analyticsService.trackEvent(AnalyticsEvent.VIEW_DM_LIST, {
      //   source: 'chat_tab',
      //   userId: streamUser.uuid,
      // });
      return client;
    } catch (err) {
      logger.error('Failed to initialize Stream Chat:', err);
      setError('Failed to initialize chat. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [chatClient, isStreamAvailable, streamUser, streamToken]);

  // Initialize on mount and when dependencies change
  useEffect(() => {
    loadStreamComponents();
    if (enabled) {
      initializeStreamChat().catch(() => {
        // Errors are already logged and stored; callers handle user-facing messaging
      });
    }
  }, [enabled, initializeStreamChat]);

  // Navigate to a specific channel (create if doesn't exist)
  const navigateToChannel = useCallback(async (
    targetUserId: string,
    displayName: string
  ): Promise<any> => {
    if (!streamUser) {
      throw new Error('Chat client not initialized');
    }

    let client = chatClient;
    if (!client) {
      if (!streamToken) {
        throw new Error('Chat client not initialized');
      }
      try {
        client = await initializeStreamChat();
      } catch (err) {
        throw new Error('Failed to initialize chat client');
      }
    }

    if (!client) {
      throw new Error('Chat client not initialized');
    }

    try {
      const channel = await StreamChatService.createDirectMessageChannel(streamUser.uuid, targetUserId);
      if (!channel) {
        throw new Error('Chat client not initialized');
      }

      analyticsService.trackEvent(AnalyticsEvent.VIEW_DM_CHANNEL, {
        source: 'channel_list',
        targetUserId,
        currentUserId: streamUser.uuid,
      });

      return channel;
    } catch (err) {
      logger.error('Failed to navigate to channel:', err);
      throw new Error('Failed to open conversation. Please try again.');
    }
  }, [chatClient, streamUser, streamToken, isStreamAvailable, initializeStreamChat]);

  // Refresh channels
  const refreshChannels = useCallback(() => {
    if (chatClient) {
      initializeStreamChat();
    }
  }, [chatClient, initializeStreamChat]);

  return {
    chatClient,
    isLoading,
    error,
    isStreamAvailable,
    // Stream Chat components (null on web)
    Chat,
    ChannelList,
    Channel,
    MessageList,
    MessageInput,
    OverlayProvider,
    i18nInstance,
    // Functions
    navigateToChannel,
    refreshChannels,
  };
}; 
